import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import PublicLayout from "@/components/PublicLayout";
import JsonLd, { breadcrumb, projectListSchema } from "@/components/JsonLd";
import { MapPin, ArrowRight } from "lucide-react";

const FILTERS = [
  { key: "all", label: "All work" },
  { key: "new_construction", label: "New Construction" },
  { key: "remodel", label: "Remodels" },
  { key: "renovation", label: "Renovations" },
];

export default function Projects() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    const url = filter === "all" ? "/projects" : `/projects?category=${filter}`;
    api.get(url).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [filter]);

  return (
    <PublicLayout>
      <JsonLd id="bc-projects" data={breadcrumb([{name: "Home", url: "/"}, {name: "Projects", url: "/projects"}])} />
      {items.length > 0 && <JsonLd id="proj-list" data={projectListSchema(items)} />}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pt-20 pb-10">
        <div className="label-overline mb-4">Portfolio</div>
        <h1 className="font-display text-5xl lg:text-6xl font-bold tracking-tight max-w-3xl">Recent work across the Coastal Bend.</h1>
        <p className="text-lg mt-6 max-w-2xl leading-relaxed" style={{color:'var(--text-muted)'}}>
          A look at recent custom homes, remodels, and renovations from Corpus Christi to Rockport.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-20">
        <div className="flex flex-wrap gap-2 mb-10" data-testid="project-filters">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              data-testid={`filter-${f.key}`}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors border ${filter === f.key ? '' : 'hover:border-white/40'}`}
              style={filter === f.key
                ? { background: 'var(--accent)', color: '#0B132B', borderColor: 'var(--accent)' }
                : { background: 'transparent', color: 'var(--text-muted)', borderColor: 'rgba(255,255,255,0.1)' }}
            >
              {f.label}
            </button>
          ))}
        </div>

        {items.length === 0 ? (
          <div className="card p-12 text-center" style={{color:'var(--text-muted)'}}>No projects in this category yet — check back soon.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((p) => (
              <Link to={`/projects/${p.id}`} key={p.id} className="card overflow-hidden group" data-testid={`project-card-${p.id}`}>
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
                <div className="p-5">
                  <div className="text-xs uppercase tracking-wider mb-1.5" style={{color: 'var(--accent)'}}>{p.category.replace('_',' ')}</div>
                  <h3 className="font-display text-xl font-bold mb-1">{p.title}</h3>
                  <div className="text-sm flex items-center gap-1.5 mb-2" style={{color:'var(--text-muted)'}}>
                    <MapPin size={13}/> {p.location || 'Coastal Bend, TX'}
                  </div>
                  <div className="text-xs flex gap-3 pt-2 mt-2 border-t border-white/5" style={{color:'var(--text-muted)'}}>
                    {p.sqft && <span>{p.sqft.toLocaleString()} sqft</span>}
                    {p.completed_year && <span>· {p.completed_year}</span>}
                    <span className="ml-auto flex items-center gap-1" style={{color:'var(--accent)'}}>View <ArrowRight size={11} /></span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </PublicLayout>
  );
}
